import React from 'react';
import { BookingSession, BookingStatus } from '../../services/api/types.ts';
import styles from './CalendarView.module.css';

interface CalendarViewProps {
    sessions: BookingSession[];
    onDayClick: (date: Date) => void;
    onSessionClick: (session: BookingSession) => void;
    onMonthChange: (date: Date) => void;
    selectedSessionId: number | null;
}

const CalendarView: React.FC<CalendarViewProps> = ({
    sessions,
    onDayClick,
    onSessionClick,
    onMonthChange,
    selectedSessionId,
}) => {
    const [currentMonth, setCurrentMonth] = React.useState(new Date());
    const [selectedDay, setSelectedDay] = React.useState<Date | null>(null);

    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    const getConfirmedBookingsCount = (session: BookingSession): number => {
        if (!session.BookingRequests || !Array.isArray(session.BookingRequests)) {
            return 0;
        }
        return session.BookingRequests.filter(request => request.Status === BookingStatus.Confirmed).length;
    };

    // Группируем сессии по датам (ISO date)
    const sessionsByDate = React.useMemo(() => {
        const map: Record<string, BookingSession[]> = {};
        if (!Array.isArray(sessions)) return map;

        sessions.forEach(session => {
            const dateStr = new Date(session.StartAt).toDateString();
            if (!map[dateStr]) map[dateStr] = [];
            map[dateStr].push(session);
        });
        return map;
    }, [sessions]);

    // Получаем дни месяца
    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }

        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    const days = getDaysInMonth(currentMonth);

    const handlePrevMonth = () => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
        setCurrentMonth(newMonth);
        onMonthChange(newMonth);
    };

    const handleNextMonth = () => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
        setCurrentMonth(newMonth);
        onMonthChange(newMonth);
    };

    const hasSessionsOnDay = (date: Date) => {
        return !!sessionsByDate[date.toDateString()];
    };

    const getSessionsForDay = (date: Date) => {
        return sessionsByDate[date.toDateString()] || [];
    };

    const formatTime = (iso: string) => {
        const date = new Date(iso);
        return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className={styles.calendar}>
            {/* Заголовок месяца */}
            <div className={styles.monthHeader}>
                <button onClick={handlePrevMonth} className={styles.navButton}>‹</button>
                <h3>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                <button onClick={handleNextMonth} className={styles.navButton}>›</button>
            </div>

            {/* Сетка дней недели */}
            <div className={styles.weekdays}>
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                    <div key={day} className={styles.weekday}>{day}</div>
                ))}
            </div>

            {/* Сетка календаря */}
            <div className={styles.daysGrid}>
                {days.map((day, index) => {
                    if (!day) return <div key={index} className={styles.emptyDay} />;

                    const dayStr = day.toDateString();
                    const hasSession = hasSessionsOnDay(day);
                    const isSelected = selectedSessionId
                        ? getSessionsForDay(day).some(s => s.Id === selectedSessionId)
                        : false;

                    return (
                        <div
                            key={index}
                            className={`${styles.day} ${hasSession ? styles.hasSessions : ''} ${isSelected ? styles.selected : ''}`}
                            onClick={() => {
                                if (hasSession) {
                                    setSelectedDay(day); // 🔥 Сохраняем выбранный день
                                    onDayClick(day);     // 🔥 Уведомляем родителя (если нужно)
                                }
                            }}
                        >
                            <span className={styles.dayNumber}>{day.getDate()}</span>
                            {hasSession && <span className={styles.sessionDot}></span>}
                        </div>
                    );
                })}
            </div>

            {/* Список сессий выбранного дня — теперь работает правильно! */}
            {selectedDay && (
                <div className={styles.sessionListContainer} id="selected-day">
                    <h4 className={styles.sessionListTitle}>Выберите сеанс</h4>
                    <div className={styles.sessionList}>
                        {getSessionsForDay(selectedDay).map(session => (
                            <div
                                key={session.Id}
                                className={`${styles.sessionItem} ${session.Id === selectedSessionId ? styles.selectedSession : ''}`}
                                onClick={() => onSessionClick(session)}
                            >
                                <span className={styles.sessionTime}>
                                    {formatTime(session.StartAt)} — {formatTime(session.FinishAt)}
                                </span>
                                <span className={styles.sessionCapacity}>
                                    {getConfirmedBookingsCount(session)} / {session.MaxBookings} мест
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarView;
